import type { RegisterPayload } from "@/types";

const IAM_GRANT_TYPES = {
  passwordLogin: "login+password",
  refreshToken: "refresh_token",
} as const;

const LOGIN_TOKEN_TTL_SECONDS = 60 * 60;
const LOGIN_REFRESH_TOKEN_TTL_SECONDS = 2 * 24 * 60 * 60;
const LOGIN_TOKEN_SCOPE = "openid email profile project:default";

type FormValue = string | number | null | undefined;

function createFormPayload(values: Record<string, FormValue>): URLSearchParams {
  const payload = new URLSearchParams();

  Object.entries(values).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    payload.set(key, String(value));
  });

  return payload;
}

export function createIamUserPayload(payload: RegisterPayload) {
  return {
    username: payload.username,
    password: payload.password,
    email: payload.email,
    first_name: payload.first_name,
    last_name: payload.last_name,
    type: "user",
  };
}

export function createPasswordLoginGrantPayload(
  login: string,
  password: string,
) {
  return createFormPayload({
    grant_type: IAM_GRANT_TYPES.passwordLogin,
    login,
    password,
    scope: LOGIN_TOKEN_SCOPE,
    ttl: LOGIN_TOKEN_TTL_SECONDS,
    refresh_ttl: LOGIN_REFRESH_TOKEN_TTL_SECONDS,
  });
}

export function createRefreshGrantPayload(
  refreshToken: string,
  scope?: string,
) {
  return createFormPayload({
    grant_type: IAM_GRANT_TYPES.refreshToken,
    refresh_token: refreshToken,
    scope,
  });
}

export function createRealmRefreshGrantPayload(refreshToken: string) {
  return createRefreshGrantPayload(refreshToken);
}

export function projectTokenScope(projectId: string): string {
  return `project:${projectId}`;
}

// A scope that names no project: IAM then issues a token bound to none, which
// is how an administrator leaves every project behind.
export const NO_PROJECT_TOKEN_SCOPE = "openid email profile";
